import { getBackendUri } from '../utils/EnvConfigUtils';
import { retrieveCachedUserDetails } from './session_thunks';
import { ROLE_TYPES } from '../utils/DataConstants';
import { ConvertedImageRef } from '../utils/ConvertedImageUtils';

// Status lookup for an original whose JPEG derivative may not exist yet.
//
// `ConvertedImageRef` (hung off an entity) only ever carries READY conversions, so a photo that is
// still being converted looks exactly like a photo that will never be converted. This endpoint
// tells the two apart: an original that comes back is being converted, an original that is ABSENT
// from the response has no conversion coming and must be rendered as-is.

// `pending` (presigned, no converter yet) and `converting` (a converter has it) are both
// still-working states — the split exists so an operator can tell a dead S3 trigger from a dead
// converter, and clients are not meant to distinguish them. Which is why callers test for the
// TERMINAL statuses below rather than listing the in-flight ones: a status this build has never
// heard of must read as "still working", not as "give up".
export const CONVERTED_IMAGE_STATUS_READY = 'ready';
export const CONVERTED_IMAGE_STATUS_FAILED = 'failed';

// Distinguishes "the lookup itself failed" from "the lookup succeeded and found nothing". Only the
// second is a final answer; the first is worth retrying.
export const CONVERTED_IMAGE_LOOKUP_FAILURE = { isLookupFailure: true };
export const isConvertedImageLookupFailure = (reason: any): boolean =>
	!!reason && reason.isLookupFailure === true;

// An auth failure is not a blip. The public request portals upload through an unauthenticated
// presign endpoint while this lookup is a SecuredAction, so retrying a 401 just spends the whole
// polling budget before the local decode gets its turn.
export const CONVERTED_IMAGE_LOOKUP_UNAUTHORIZED = { isLookupUnauthorized: true };
export const isConvertedImageLookupUnauthorized = (reason: any): boolean =>
	!!reason && reason.isLookupUnauthorized === true;

/** False on the public portals, where there is no session to authenticate the lookup with. */
export const canLookupConvertedImages = (): boolean => !!retrieveCachedUserDetails(['token']).token;

export const isTerminalConvertedImageStatus = (status?: string | null): boolean =>
	status === CONVERTED_IMAGE_STATUS_READY || status === CONVERTED_IMAGE_STATUS_FAILED;

export interface ConvertedImageStatusRef extends ConvertedImageRef {
	status: string;
}

export interface ConvertedImageLookupRequest {
	fileIds?: string[];
	s3ObjectKeys?: string[];
}

// The backend rejects a longer list with a 400.
export const CONVERTED_IMAGE_LOOKUP_MAX_IDS = 200;

// A 50ms window rather than a per-request POST: every tile on a media panel polls on its own
// schedule, and their ticks land close enough together to merge into one round trip.
const COALESCE_WINDOW_MS = 50;
// A hung lookup must not strand the poller: with no abort the promise never settles, so the store
// keeps `inFlight` set, schedules no further tick, and holds every tile on the placeholder.
const LOOKUP_TIMEOUT_MS = 10000;

const getUserTypeSegment = (): string =>
	retrieveCachedUserDetails(['userType']).userType === ROLE_TYPES.SUPPLIER
		? ROLE_TYPES.SUPPLIER
		: ROLE_TYPES.BUYER;

const chunkIds = (ids: string[]): string[][] => {
	const chunks: string[][] = [];
	for (let index = 0; index < ids.length; index += CONVERTED_IMAGE_LOOKUP_MAX_IDS) {
		chunks.push(ids.slice(index, index + CONVERTED_IMAGE_LOOKUP_MAX_IDS));
	}
	return chunks;
};

const dedupeIds = (ids?: string[] | null): string[] =>
	Array.from(new Set((ids || []).filter((id) => !!id)));

// Deliberately a bare fetch rather than FetchUtils.post: this is a read that happens to be a POST,
// and FetchUtils blocks every non-GET for read-only / impersonating users with an error toast.
const postLookup = (request: ConvertedImageLookupRequest): Promise<ConvertedImageStatusRef[]> => {
	const controller = new AbortController();
	const abortTimeoutId = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
	return (
		fetch(`${getBackendUri()}/api/v1/${getUserTypeSegment()}/image/converted_images/lookup`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				'X-Auth-Token': retrieveCachedUserDetails(['token']).token,
			},
			body: JSON.stringify(request),
			signal: controller.signal,
		})
			.then((response) => {
				if (response.ok) {
					return response.json();
				}
				return Promise.reject(
					response.status === 401 || response.status === 403
						? CONVERTED_IMAGE_LOOKUP_UNAUTHORIZED
						: new Error(`HTTP ${response.status}`)
				);
			})
			.then((json) => (json && Array.isArray(json.data) ? json.data : []))
			// Marked rather than swallowed. Returning [] made a network blip indistinguishable from a
			// successful "nothing to convert", which the store caches in `resolvedKeys` for the whole
			// tab — so one dropped request permanently hid a derivative that had in fact landed. An
			// abort lands here too, so a timed-out lookup retries inside the polling budget; an auth
			// failure passes through unmarked so the store stops instead of retrying it.
			.catch((reason) =>
				Promise.reject(
					isConvertedImageLookupUnauthorized(reason) ? reason : CONVERTED_IMAGE_LOOKUP_FAILURE
				)
			)
			.finally(() => clearTimeout(abortTimeoutId))
	);
};

const lookupConvertedImageStatuses = (
	request: ConvertedImageLookupRequest
): Promise<ConvertedImageStatusRef[]> => {
	const fileIds = dedupeIds(request.fileIds);
	const s3ObjectKeys = dedupeIds(request.s3ObjectKeys);
	if (fileIds.length === 0 && s3ObjectKeys.length === 0) {
		return Promise.resolve([]);
	}
	const requests: ConvertedImageLookupRequest[] = chunkIds(fileIds)
		.map((fileIdChunk) => ({ fileIds: fileIdChunk }))
		.concat(chunkIds(s3ObjectKeys).map((keyChunk) => ({ s3ObjectKeys: keyChunk })));
	return Promise.all(requests.map(postLookup)).then((results) =>
		results.reduce<ConvertedImageStatusRef[]>((accumulated, refs) => accumulated.concat(refs), [])
	);
};

interface QueuedLookup {
	request: ConvertedImageLookupRequest;
	resolve: (refs: ConvertedImageStatusRef[]) => void;
	reject: (reason: unknown) => void;
}

let queuedLookups: QueuedLookup[] = [];
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;

const flushQueuedLookups = () => {
	const batched = queuedLookups;
	queuedLookups = [];
	flushTimeoutId = null;
	const fileIds = batched.reduce<string[]>(
		(accumulated, queued) => accumulated.concat(queued.request.fileIds || []),
		[]
	);
	const s3ObjectKeys = batched.reduce<string[]>(
		(accumulated, queued) => accumulated.concat(queued.request.s3ObjectKeys || []),
		[]
	);
	const resolveAll = (refs: ConvertedImageStatusRef[]) =>
		batched.forEach((queued) => queued.resolve(refs));
	// A failed batch rejects every caller rather than handing them an empty result, so the poller
	// can retry instead of recording a permanent miss. Either way every queued caller settles: a
	// promise that never settles strands its poller on the placeholder.
	const rejectAll = (reason: unknown) => batched.forEach((queued) => queued.reject(reason));
	try {
		lookupConvertedImageStatuses({ fileIds, s3ObjectKeys }).then(resolveAll).catch(rejectAll);
	} catch (error) {
		rejectAll(CONVERTED_IMAGE_LOOKUP_FAILURE);
	}
};

/**
 * Same contract as `lookupConvertedImageStatuses`, but merges every call made within a short
 * window into a single round trip. Each caller is handed the whole batch's refs and picks out
 * its own by fileId / s3ObjectKey, so a caller never sees fewer refs than it asked about.
 */
export const lookupConvertedImageStatusesBatched = (
	request: ConvertedImageLookupRequest
): Promise<ConvertedImageStatusRef[]> =>
	new Promise((resolve, reject) => {
		queuedLookups.push({ request, resolve, reject });
		if (flushTimeoutId === null) {
			flushTimeoutId = setTimeout(flushQueuedLookups, COALESCE_WINDOW_MS);
		}
	});
